import React, { FC, PropsWithChildren } from 'react';
import uniqueId from 'lodash/uniqueId';
import noop from 'utils/noop';
import { Checkbox, CHECKBOX_STATUS } from 'ui/checkbox';
import { HelpToggletip, TipPlacement } from 'ui/tooltip';
import { colors } from 'ui/theme';
import {
  ToggleSectionWrapper,
  ToggleContentWrapper,
  ToggleDetails,
  CheckboxWrapper,
  CheckboxLabel,
} from './ToggleSection.styles';

interface ToggleSectionProps {
  /** Is the toggle set to true or false? */
  isActive?: boolean;
  /** Is the toggle section readonly? */
  isReadOnly?: boolean;
  /** Text appearing in the info toggle tip */
  infoText?: string;
  /** Id for the input text */
  inputId?: string;
  /** Text for the label */
  labelText?: string;
  /**
   * Component to manually set the label element.
   * This provides the flexibility to set the section the way you would like.
   * Add/remove elements etc.
   *
   * NOTE: Make sure to follow the below rules:
   * 1. Use the ToggleLabel component if you would like to maintain label styling
   * 2. Add `htmlFor={inputId}` to label/ToggleLabel for ADA purposes
   *
   * @example
   * ```javascript
   * <ToggleSection
   *  inputId="my-input"
   *  LabelSection={
   *    <>
   *      <SomeElement />
   *      <ToggleLabel htmlFor="my-input">I'm a label!</ToggleLabel>
   *      <Another Element />
   *    </>
   *  }
   * />
   * ```
   *
   * @example
   * ```javascript
   * <ToggleSection
   *  inputId="my-input"
   *  LabelSection={<label htmlFor="my-input">I'm a label!</label>}
   * />
   * ```
   */
  LabelSection?: React.ReactNode;
  /** Callback when toggle is changed */
  onChange?: (isActive: boolean) => void;
}

export const ToggleSection: FC<PropsWithChildren<ToggleSectionProps>> = ({
  children,
  isActive = false,
  isReadOnly = false,
  infoText,
  inputId,
  labelText = '',
  LabelSection,
  onChange = noop,
}) => {
  const id = inputId || uniqueId('toggle-section-checkbox_');
  const label = LabelSection || labelText;

  return (
    <ToggleSectionWrapper>
      <ToggleContentWrapper>
        <CheckboxWrapper isDisabled={isReadOnly}>
          <Checkbox
            id={id}
            data-test={id}
            status={isActive ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
            isDisabled={isReadOnly}
            onClick={() => onChange(!isActive)}
          />
          <CheckboxLabel htmlFor={id}>{label}</CheckboxLabel>
          {infoText && (
            <HelpToggletip
              placement={TipPlacement.Right}
              tooltipContent={infoText}
              iconProps={{
                color: colors.secondaryText,
              }}
            />
          )}
        </CheckboxWrapper>
        {isActive && <ToggleDetails>{children}</ToggleDetails>}
      </ToggleContentWrapper>
    </ToggleSectionWrapper>
  );
};

export default ToggleSection;
